import { createTheme } from '@mui/material/styles';

// Core theme properties
export const baseTheme = {
    // Standard MUI Properties
    palette: {
        mode: 'light',
        primary: {
            dark: "#2b5b8a",
            main: "#2b5b8a",
            light: "#deefff",
            contrastText: "#fff"
        },
        secondary: {
            dark: "#262834",
            main: "#757781",
            light: "#f8f8f8",
            contrastText: "#fff"
        },
        error: {
            dark: "#aa0000",
            main: "#a90c0c",
            light: "#ffe4e1",
            contrastText: "#fff"
        },
        warning: {
            dark: "#cc7e00",
            main: "#ff9800",
            light: "#fff5de",
            contrastText: "#fff"
        },
        info: {
            dark: "#174D73",
            main: "#2171a6",
            light: "#e8f6ff",
            contrastText: "#fff"
        },
        success: {
            dark: "#365922",
            main: "#639645",
            light: "#f6fff0",
            contrastText: "#fff"
        },
        background: {
            paper: "#ffffff",
            default: "#efeeee"
        },
        text: {
            primary: "#363537",
            secondary: "rgba(0, 0, 0, 0.6)",
            disabled: "rgba(0, 0, 0, 0.38)"
        },
        divider: "rgba(0, 0, 0, 0.12)",
        action: {
            active: "rgba(0, 0, 0, 0.54)",
            hover: "rgba(0, 0, 0, 0.04)",
            selected: "rgba(0, 0, 0, 0.08)",
            disabled: "rgba(0, 0, 0, 0.26)",
            disabledBackground: "rgba(0, 0, 0, 0.12)",
            focus: "rgba(0, 0, 0, 0.12)",
            focusOpacity: 0.12,
            hoverOpacity: 0.04,
            selectedOpacity: 0.08,
            disabledOpacity: 0.38,
            activatedOpacity: 0.12,
            activeBackground: "rgba(0, 0, 0, 0.03)"
        },
        // Custom palette colors (non-standard but used in codebase)
        light: {
            dark: "#cccccc",
            main: "#efefef",
            light: "#f8f8f8",
            contrastText: "#333333"
        },
        accent: {
            dark: "#401089",
            main: "#6002ee",
            light: "#efe5fd",
            contrastText: "#ffffff"
        }
    },
    typography: {
        fontFamily: "Aptos, Arial",
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: {
            fontSize: "1.8rem",
            fontFamily: "Aptos, Arial",
            fontWeight: 500,
            lineHeight: 1.2
        },
        h2: {
            fontSize: "1.5rem",
            fontFamily: "Aptos, Arial",
            fontWeight: 500,
            lineHeight: 1.3
        },
        h3: {
            fontSize: "1.3rem",
            fontFamily: "Aptos, Arial",
            fontWeight: 500,
            lineHeight: 1.7
        },
        h4: {
            fontSize: "1.2rem",
            fontFamily: "Aptos, Arial",
            fontWeight: 500,
            lineHeight: 1.4
        },
        h5: {
            fontSize: "1.1rem",
            fontFamily: "Aptos, Arial",
            fontWeight: 500,
            lineHeight: 1.5
        },
        h6: {
            fontSize: "1rem",
            fontFamily: "Aptos, Arial",
            fontWeight: 500,
            lineHeight: 1.6
        },
        body1: {
            fontSize: "1rem",
            lineHeight: 1.5
        },
        body2: {
            fontSize: "0.875rem",
            lineHeight: 1.5
        }
    },
    // Custom Properties (non-standard MUI, but used in codebase)
    body: "#efeeee",
    card: {
        boxShadow: "0 0 2px 0 rgba(0,0,0,.1)",
        borderRadius: "8px"
    },
    link: {
        hover: "#00324d",
        active: "#00517c"
    },
    text: {
        dark: "#363537",
        error: "#ff0000",
        light: "#ffffff",
        success: "#8eb417",
        warning: "#e56305",
        link: "#00517c"
    },
    appBar: {
        color: "#ffffff",
        zIndex: 1201,
        borderBottom: "1px solid #ddd",
        backgroundColor: "#385da9"
    },
    container: {
        Shadow: "0 0 5px 0 rgba(0,0,0,.1)",
        bgColor: "#ffffff"
    },
    rightDrawer: {
        width: 500
    },
    authBackground: "radial-gradient(at 50% 30%, #D7E1EC 10%, #FFFFFF 100%)",
    verticalNavbar: {
        color: "#5c9df5",
        bgColorDark: "#25282e",
        bgColorLight: "#2f323a"
    }
};

const authLightTheme = {
    ...baseTheme,
    palette: {
        ...baseTheme.palette,
        info: {
            main: "#2171a6",
            light: "#3f9ad5",
            dark: "#0c4469",
            contrastText: "#fff",
        },
        primary: {
            main: "#2b5b8a",
            light: "#2b5b8a",
            dark: "#2b5b8a",
            contrastText: "#fff",
        },
        success: {
            main: "#639645",
            light: "#88bd66",
            dark: "#365922",
            contrastText: "#fff",
        },
        error: {
            main: "#a90c0c",
            light: "#ff6659",
            dark: "#aa0000",
            contrastText: "#fff",
        },
        warning: {
            main: "#ff9800",
            light: "#ffbf33",
            dark: "#cc7e00",
            contrastText: "#fff",
        },
        secondary: {
            main: "#757781",
            light: "#cdcdce",
            dark: "#262834",
            contrastText: "#fff",
        },
        type: 'light',
        background: {
            default: "#efeeee",
            paper: "#ffffff",
        },
    },
    appBar: {
        backgroundColor: '#3F4A56',
        color: '#2f3239',
        borderBottom: '1px solid #ddd',
        zIndex: 1201,
    },
    auth: {
        loginBgColor: '#005b9d',
        registerBgColor: '#3F4A56',
        forgotBgColor: '#3F4A56',
    },
    text: {
        light: '#ffffff',
        dark: '#363537',
        error: '#ff0000',
        warning: '#e56305',
        success: '#8eb417',
    },
    link: {
        active: '#00517c',
        hover: '#00324d',
    },
    components: {
        MuiPickersPopper: {
            styleOverrides: {
                root: {
                    zIndex: '9999!important',
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    // Some CSS
                    backgroundColor: '#01a3ff',
                    color: '#ffffff',
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: '#ff0000',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    color: '#333333',
                    "& .MuiCardHeader-root": {
                        borderBottom: '1px solid #ddd'
                    },
                    '& .MuiCardContent-root, & .MuiCardContent-root:last-child': {
                        paddingBottom: 0,
                    },
                    "& .MuiCardActions-root": {
                        borderTop: '1px solid #eff2f5',
                        background: 'transparent',
                        '& a': {
                            textDecoration: 'none'
                        }
                    }
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'accountButton' },
                    style: ({ theme }) => ({
                        border: '1px solid',
                        textTransform: 'none',
                        borderColor: theme.palette.secondary.dark,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            borderColor: theme.palette.secondary.light,
                            color: theme.palette.secondary.contrastText,
                            '& .MuiTypography-root': {
                                color: theme.palette.secondary.contrastText,
                            }
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.secondary.contrastText,
                        }
                    }),
                },
            ],
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    '& p': {
                        marginBottom: '0!important',
                    }
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    '& .MuiDrawer-paper': {
                        position: 'relative'
                    },
                    '& .MuiCardContent-root': {
                        padding: '1rem',
                    }
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root': {
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiList-root': {
                        padding: '0',
                        '& a': {
                            color: '#000',
                            '& li': {
                                padding: '10px 20px',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                }
                            }
                        },
                        '& svg': {
                            marginRight: '10px',
                            fontSize: '1rem'
                        },
                    },
                }
            }
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#f8f8f8',
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#444444',
                            fontWeight: '300',
                        }
                    },
                    '& .MuiDataGrid-pinnedColumns': {
                        backgroundColor: '#ffffff',
                        '& .MuiDataGrid-cell': {
                            backgroundColor: '#ffffff',
                            '& .MuiDataGrid-actionsCell': {
                                zIndex: 12,
                            }
                        }
                    },
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiDialog-paper': {
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiDialogContent-root': {
                        padding: '1rem',
                        paddingTop: '1rem!important',
                    },
                    '& .MuiDialogTitle-root': {
                        background: '#f8f8f8',
                        borderBottom: '1px solid #f2f2f2',
                    },
                    '& .MuiDialogActions-root': {
                        padding: '0 24px 24px'
                    }
                }
            }
        }
    },
}

const authDarkTheme = {
    ...baseTheme,
    palette: {
        ...baseTheme.palette,
    }
}

// All custom properties are available for customization
export const publicLightTheme = {
    ...baseTheme,
    palette: {
        mode: 'light',
        primary: {
            dark: "#1a6ba8",
            main: "#007bff",
            light: "#4a9eff",
            contrastText: "#fff"
        },
        secondary: {
            dark: "#262834",
            main: "#757781",
            light: "#f8f8f8",
            contrastText: "#fff"
        },
        error: {
            dark: "#b91c1c",
            main: "#dc3545",
            light: "#ffe4e1",
            contrastText: "#fff"
        },
        warning: {
            dark: "#b45309",
            main: "#ffc107",
            light: "#fff5de",
            contrastText: "#000"
        },
        info: {
            dark: "#0ea5e9",
            main: "#17a2b8",
            light: "#e8f6ff",
            contrastText: "#fff"
        },
        success: {
            dark: "#8fb03a",
            main: "#a3c043",
            light: "#f6fff0",
            contrastText: "#000"
        },
        background: {
            paper: "#ffffff",
            default: "#f8f9fa"
        },
        text: {
            primary: "#212529",
            secondary: "#6c757d",
            disabled: "rgba(0, 0, 0, 0.38)"
        },
        divider: "#dee2e6",
        action: {
            active: "rgba(0, 0, 0, 0.54)",
            hover: "rgba(0, 0, 0, 0.04)",
            selected: "rgba(0, 0, 0, 0.08)",
            disabled: "rgba(0, 0, 0, 0.26)",
            disabledBackground: "rgba(0, 0, 0, 0.12)",
            focus: "rgba(0, 0, 0, 0.12)",
            focusOpacity: 0.12,
            hoverOpacity: 0.04,
            selectedOpacity: 0.08,
            disabledOpacity: 0.38,
            activatedOpacity: 0.12,
            activeBackground: "rgba(0, 0, 0, 0.03)"
        },
        // Custom palette colors (non-standard but used in codebase)
        light: {
            dark: "#cccccc",
            main: "#efefef",
            light: "#f8f8f8",
            contrastText: "#333333"
        },
        accent: {
            dark: "#401089",
            main: "#6002ee",
            light: "#efe5fd",
            contrastText: "#ffffff"
        }
    },
    // Custom Properties - Extracted from CSS variables (light theme)
    body: "#f8f9fa",
    card: {
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        borderRadius: "8px"
    },
    link: {
        hover: "#0056b3",
        active: "#004085"
    },
    text: {
        dark: "#212529",
        error: "#dc3545",
        light: "#ffffff",
        success: "#a3c043",
        warning: "#ffc107",
        link: "#007bff"
    },
    appBar: {
        color: "#333333",
        zIndex: 1201,
        borderBottom: "1px solid #dee2e6",
        backgroundColor: "#ffffff"
    },
    container: {
        Shadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        bgColor: "#ffffff"
    },
    rightDrawer: {
        width: 500
    },
    authBackground: "radial-gradient(at 50% 30%, #D7E1EC 10%, #FFFFFF 100%)",
    verticalNavbar: {
        color: "#17a2b8",
        bgColorDark: "#212529",
        bgColorLight: "#f8f9fa"
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: "#ffffff",
                    color: "#232323",
                    borderBottom: "1px solid rgb(255, 255, 255)",
                    zIndex: 1201,
                },
            },
        },
    },
};

export const publicDarkTheme = {
    ...baseTheme,
    palette: {
        mode: 'dark',
        primary: {
            dark: "#1a6ba8",
            main: "#007bff",
            light: "#93c5fd",
            contrastText: "#fff"
        },
        secondary: {
            dark: "#262834",
            main: "#9ca3af",
            light: "#d1d5db",
            contrastText: "#fff"
        },
        error: {
            dark: "#b91c1c",
            main: "#f87171",
            light: "#fca5a5",
            contrastText: "#fff"
        },
        warning: {
            dark: "#b45309",
            main: "#fbbf24",
            light: "#fcd34d",
            contrastText: "#000"
        },
        info: {
            dark: "#0ea5e9",
            main: "#22d3ee",
            light: "#67e8f9",
            contrastText: "#fff"
        },
        success: {
            dark: "#8fb03a",
            main: "#a3c043",
            light: "#b8d04c",
            contrastText: "#000"
        },
        background: {
            paper: "#242424",
            default: "#181818"
        },
        text: {
            primary: "#ffffff",
            secondary: "#b0b0b0",
            disabled: "rgba(255, 255, 255, 0.38)"
        },
        divider: "rgba(255, 255, 255, 0.12)",
        action: {
            active: "rgba(255, 255, 255, 0.54)",
            hover: "rgba(255, 255, 255, 0.08)",
            selected: "rgba(255, 255, 255, 0.16)",
            disabled: "rgba(255, 255, 255, 0.26)",
            disabledBackground: "rgba(255, 255, 255, 0.12)",
            focus: "rgba(96, 165, 250, 0.3)",
            focusOpacity: 0.3,
            hoverOpacity: 0.08,
            selectedOpacity: 0.16,
            disabledOpacity: 0.38,
            activatedOpacity: 0.12,
            activeBackground: "rgba(255, 255, 255, 0.06)"
        },
        light: {
            dark: "#4a4a4a",
            main: "#2d2d2d",
            light: "#404040",
            contrastText: "#ffffff"
        },
        accent: {
            dark: "#401089",
            main: "#8b5cf6",
            light: "#a78bfa",
            contrastText: "#ffffff"
        }
    },
    body: "#121212",
    card: {
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
        borderRadius: "8px"
    },
    link: {
        hover: "#93c5fd",
        active: "#60a5fa"
    },
    text: {
        dark: "#ffffff",
        error: "#f87171",
        light: "#ffffff",
        success: "#a3c043",
        warning: "#fbbf24",
        link: "#60a5fa"
    },
    appBar: {
        color: "#ffffff",
        zIndex: 1201,
        borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        backgroundColor: "#242424"
    },
    container: {
        Shadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
        bgColor: "#242424"
    },
    rightDrawer: {
        width: 500
    },
    authBackground: "radial-gradient(at 50% 30%, #181818 10%, #242424 100%)",
    verticalNavbar: {
        color: "#60a5fa",
        bgColorDark: "#181818",
        bgColorLight: "#242424"
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: "#242424",
                    color: "#ffffff",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
                    zIndex: 1201,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: "#242424",
                    color: "#ffffff",
                    "& .MuiCardHeader-root": {
                        borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                        color: "#ffffff",
                    },
                    "& .MuiCardContent-root": {
                        color: "#ffffff",
                    },
                    "& .MuiCardActions-root": {
                        borderTop: "1px solid rgba(255, 255, 255, 0.12)",
                        backgroundColor: "transparent",
                    }
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    "&.MuiButton-containedPrimary": {
                        backgroundColor: "#60a5fa",
                        color: "#ffffff",
                        "&:hover": {
                            backgroundColor: "#93c5fd",
                        }
                    },
                    "&.MuiButton-outlinedPrimary": {
                        borderColor: "#60a5fa",
                        color: "#60a5fa",
                        "&:hover": {
                            borderColor: "#93c5fd",
                            backgroundColor: "rgba(96, 165, 250, 0.08)",
                        }
                    }
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiInputBase-root": {
                        color: "#ffffff",
                    },
                    "& .MuiInputLabel-root": {
                        color: "#b0b0b0",
                        "&.Mui-focused": {
                            color: "#60a5fa",
                        }
                    },
                    "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.23)",
                        },
                        "&:hover fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.4)",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#60a5fa",
                        }
                    }
                },
            },
        },
    },
};

export const coreTheme = {
    ...baseTheme,
    components: {
        MuiPickersPopper: {
            styleOverrides: {
                root: {
                    zIndex: '9999!important',
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#273045',
                    color: '#ffffff',
                    '& .MuiToolbar-root': {
                        '& .MuiIconButton-root': {
                            color: '#ffffff',
                        }
                    }
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: '#ff0000',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: '#333333',
                    boxShadow: theme?.card?.boxShadow || '0 0 2px 0 rgb(0 0 0 / 10%)',
                    borderRadius: theme?.card?.borderRadius || '8px',
                    "& .MuiCardHeader-root": {
                        padding: '1.5rem',
                        borderBottom: '1px solid #eff2f5',
                    },
                    '& .MuiCardContent-root': {
                        padding: '1.5rem',
                    },
                    '& .MuiCardContent-root:last-child': {
                        paddingBottom: '1.5rem',
                    },
                    "& .MuiCardActions-root": {
                        padding: '1rem',
                        borderTop: '1px solid #eff2f5',
                        background: 'transparent',
                        '& a': {
                            textDecoration: 'none'
                        }
                    }
                }),
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'accountButton' },
                    style: ({ theme }) => ({
                        border: `1px solid ${theme.palette.secondary.dark}`,
                        textTransform: 'none',
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            borderColor: theme.palette.secondary.light,
                            color: theme.palette.secondary.contrastText,
                            '& .MuiTypography-root': {
                                color: theme.palette.secondary.contrastText,
                            }
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.secondary.contrastText,
                        }
                    }),
                },
            ],
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                    // Check if the variant is 'text' (which is standard in MUI by default)
                    if (ownerState.variant === 'standard') {
                        const colorVariant = ownerState.color;
                        return {
                            textTransform: 'none',
                            backgroundColor: theme.palette[colorVariant]?.light, // Use light color from the theme
                            '&:hover': {
                                backgroundColor: theme.palette[colorVariant]?.dark || theme.palette.grey[500], // Use dark color for hover
                                color: theme.palette[colorVariant]?.contrastText || theme.palette.common.white, // Use contrast text color
                            },
                            '&.Mui-disabled': {
                                backgroundColor: theme.palette.action.disabled, // Disabled button color
                                color: theme.palette.action.disabledBackground, // Disabled text color
                            },
                        };
                    }

                    // Return empty object for other variants to keep default behavior
                    return {};
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                    if (ownerState.variant === 'filled') {
                        return {
                            fontSize: '0.70rem',
                            borderRadius: '4px',
                            backgroundColor: theme.palette[ownerState.color]?.main || theme.palette.grey[500],
                            color: theme.palette[ownerState.color]?.contrastText || theme.palette.common.white,
                            '&:hover': {
                                backgroundColor: theme.palette[ownerState.color]?.dark || theme.palette.grey[700],
                            },
                        };
                    } else if (ownerState.variant === 'outlined') {
                        return {
                            fontSize: '0.70rem',
                            borderRadius: '4px',
                            border: `1px solid ${theme.palette[ownerState.color]?.main || theme.palette.grey[500]}`,
                            color: theme.palette[ownerState.color]?.main || theme.palette.grey[500],
                            backgroundColor: theme.palette[ownerState.color]?.light || theme.palette.grey[300],
                            '&:hover': {
                                backgroundColor: theme.palette[ownerState.color]?.light || theme.palette.grey[300],
                                borderColor: theme.palette[ownerState.color]?.dark || theme.palette.grey[700],
                            },
                        };
                    }
                },
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#f9f9f9',
                        '& fieldset': {
                            border: '1px solid #ddd',
                        },
                        '&:hover fieldset': {
                            border: `2px solid ${theme.palette.primary.main}`,
                        },
                        '&:hover': {
                            backgroundColor: '#f8f8f8',
                        },
                        '&.Mui-focused': {
                            backgroundColor: '#f6f8ff',
                        },
                        '&.Mui-focused fieldset': {
                            border: `2px solid ${theme.palette.primary.main}`,
                        },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: `2px solid ${theme.palette.primary.main}`,
                    },
                }),
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '& .MuiSelect-select': {
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #ddd',
                    },
                    '&:hover .MuiOutlinedInput-root': {
                        backgroundColor: '#eaeaea',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #a1a1a1',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                        border: '2px solid #6200ea',
                        backgroundColor: '#e0e0e0',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid #6200ea',
                    },
                    '& .MuiInputBase-input': {
                        color: '#333',
                    },
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    '& p': {
                        marginBottom: '0!important',
                    }
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    '& .MuiDrawer-paper': {
                        position: 'relative'
                    },
                    '& .MuiCardContent-root': {
                        padding: '1rem',
                    }
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                root: {
                    zIndex: '9999!important',
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: '#00517c',
                    '&:hover': {
                        textDecoration: 'underline',
                        color: '#00324d',
                    },
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                root: {
                    '& .MuiPaper-root': {
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiList-root': {
                        padding: '0',
                        '& a': {
                            color: '#000',
                            '& li': {
                                padding: '10px 20px',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                }
                            }
                        },
                        '& svg': {
                            marginRight: '10px',
                            fontSize: '1rem'
                        },
                    },
                }
            }
        },
        MuiDataGrid: {
            styleOverrides: {
                cell: {
                    display: 'flex',
                    alignItems: 'center',
                },
                root: {
                    border: 'none',
                    '& .MuiDataGrid-iconSeparator': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none',
                        '& .MuiDataGrid-row--borderBottom': {
                            background: 'transparent',
                        }
                    },
                    '& .MuiDataGrid-columnHeader': {
                        outline: 'none',
                        border: 'none',
                        '&:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#888888',
                            fontSize: '0.70rem',
                            textTransform: 'uppercase',
                            fontWeight: '600',
                        },
                    },
                    '& .MuiDataGrid-pinnedColumns': {
                        '& .MuiDataGrid-cell': {
                            '& .MuiDataGrid-actionsCell': {
                                zIndex: 12,
                            },
                        },
                    },
                    '& .MuiDataGrid-row': {
                        borderBottom: '1px dashed #eeeeee',
                    },
                    '& .MuiDataGrid-cell': {
                        border: 'none',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        border: 'none',
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: ({ theme }) => ({
                    border: 'none',
                    '& .MuiTableHead-root': {
                        '& .MuiTableRow-root': {
                            borderBottom: 'none',
                            '& .MuiTableCell-root': {
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                color: '#aaaaaa',
                                padding: '16px',
                                borderBottom: 'none',
                            },
                        },
                    },
                    '& .MuiTableRow-root': {
                        '& .MuiTableCell-root': {
                            borderBottom: '1px dashed #eeeeee',
                            padding: '16px',
                            fontSize: '1rem',
                            color: '#666666',
                        },
                    },
                    '& .MuiTableCell-root': {
                        padding: '12px',
                        fontSize: '1rem',
                        color: '#555555',
                    },
                    '& .MuiTableBody-root': {
                        '& .MuiTableRow-root': {
                            transition: 'background-color 0.3s',
                            '&:last-of-type .MuiTableCell-root': {
                                borderBottom: 'none',
                            },
                        },
                    },
                }),
            },
        },
        MuiDialog: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiDialog-paper': {
                        borderRadius: '4px',
                        boxShadow: '0 0 10px 0 rgb(0 0 0 / 10%)',
                    },
                    '& .MuiDialogContent-root': {
                        padding: '2rem',
                        '& .MuiTableContainer-root': {
                            marginTop: theme.spacing(2),
                            '& .MuiTable-root': {
                                '& .MuiTableHead-root': {
                                    '& .MuiTableRow-root': {
                                        '& .MuiTableCell-root': {
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                        },
                                    },
                                },
                                '& .MuiTableBody-root': {
                                    '& .MuiTableRow-root': {
                                        '& .MuiTableCell-root': {
                                            fontSize: '0.8rem',
                                            '& .MuiAvatar-root': {
                                            },
                                            '& .employee_name': {
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                            },
                                            '& .employee_code': {
                                                fontSize: '0.8rem',
                                                fontWeight: 400,
                                            },
                                        },
                                    },
                                },
                                '& .MuiTableFooter-root': {
                                },
                            },
                        },
                    },
                    '& .MuiDialogTitle-root': {
                        background: '#f8f8f8',
                        borderBottom: '1px solid #f2f2f2',
                    },
                    '& .MuiDialogActions-root': {
                        paddingTop: '1rem',
                        padding: '0 2rem 2rem',
                        margin: 0
                    }
                }),
            }
        },
    },
}

export const theme = {
    authLightTheme: createTheme(authLightTheme),
    authDarkTheme: createTheme(authDarkTheme),
    coreTheme: createTheme(coreTheme),
    publicLightTheme: createTheme(publicLightTheme),
    publicDarkTheme: createTheme(publicDarkTheme),
};
